import WithSuspense from "../components/Loaders/WithSuspense";
import { PRIVATE_PATHS, PUBLIC_PATHS } from "./constants";
import { Navigate, useLocation } from "react-router-dom";
import { lazy } from "react";
import { AppRoute } from "../types/app";


const {
  LOGIN,
  FORGOT_PASS,
  REGISTER,
  COMPANY_INFO,
  FINISH_SETUP,
  VERIFY_OTP,
  CHANGE_PASSWORD,
} = PUBLIC_PATHS;
const {
  DASHBOARD,
  AGENCY_TALENTS,
  WORK_STREAMS,
  VIEW_STREAMS_DETAILS,
  OFFERS,
  REVIEWS,
  EARNINGS,
  CLIENTS,
  CALLS,
  CHATS,
  COMPANY_DETAILS,
  PUBLIC_PROFILE,
  SETTINGS,
  PROFILE_SAVED,
  AGENCY_TALENTS_BASIC_INFO,
  AGENCY_TALENTS_PERSONAL_INFO,
  AGENCY_TALENTS_INTRO_VIDEO,
  AGENCY_TALENTS_PAY_SETTINGS,
  VIEW_AGENCY_TALENT,
  EDIT_AGENCY_TALENT,
  UPDATE_AGENCY_TALENT,
} = PRIVATE_PATHS;

const Login = WithSuspense(lazy(() => import("../pages/Authentication/Login")));
const Register = WithSuspense(
  lazy(() => import("../pages/Authentication/Register")),
);
const AccountType = WithSuspense(
  lazy(() => import("../pages/Authentication/AccountType")),
);

const ForgotPassword = WithSuspense(
  lazy(() => import("../pages/Authentication/ForgotPassword")),
);

const VerifyOtp = WithSuspense(
  lazy(() => import("../pages/Authentication/VerifyOtp")),
);

const ChangePassword = WithSuspense(
  lazy(() => import("../pages/Authentication/ChangePassword")),
);

const CompanyInfo = WithSuspense(
  lazy(() => import("../pages/Authentication/CompanyInfo")),
);

const FinishSetup = WithSuspense(
  lazy(() => import("../pages/Authentication/FinishSetup")),
);

const Dashboard = WithSuspense(
  lazy(() => import("../pages/Dashboard/Dashboard")),
);

const AgencyTalents = WithSuspense(
  lazy(() => import("../pages/AgencyTalents/AgencyTalents")),
);

const AgencyTalentsBasicInfo = WithSuspense(
  lazy(() => import("../pages/AgencyTalents/Onboarding/BasicInfo")),
);

const AgencyTalentsPersonalInfo = WithSuspense(
  lazy(
    () => import("../pages/AgencyTalents/Onboarding/ProfessionalExperience"),
  ),
);

const AgencyTalentsIntroVideo = WithSuspense(
  lazy(() => import("../pages/AgencyTalents/Onboarding/IntroVideo")),
);

const AgencyTalentsPaySetting = WithSuspense(
  lazy(() => import("../pages/AgencyTalents/Onboarding/PaySettings")),
);

const EditTalentLayout = WithSuspense(
  lazy(() => import("../pages/AgencyTalents/Onboarding/EditTalentLayout")),
);

const Offers = WithSuspense(lazy(() => import("../pages/Offers/Offers")));

const WorkStreams = WithSuspense(
  lazy(() => import("../pages/WorkStreams/WorkStreams")),
);

const ViewWorkStreamsDetails = WithSuspense(
  lazy(() => import("../pages/WorkStreams/ViewWorkStreamsDetails")),
);

const Reviews = WithSuspense(lazy(() => import("../pages/Reviews/Reviews")));

const Earnings = WithSuspense(lazy(() => import("../pages/Earnings/Earnings")));

const Calls = WithSuspense(lazy(() => import("../pages/Calls/Calls")));

const Chats = WithSuspense(lazy(() => import("../pages/Chats/Chats")));

const Clients = WithSuspense(lazy(() => import("../pages/Clients/Clients")));

const SettingsLayout = WithSuspense(
  lazy(() => import("../pages/Settings/Settings")),
);

const SettingsProfile = WithSuspense(
  lazy(() => import("../pages/Settings/tabs/Profile")),
);

const SettingsUsers = WithSuspense(
  lazy(() => import("../pages/Settings/tabs/Users")),
);

const SettingsAuthSignatory = WithSuspense(
  lazy(() => import("../pages/Settings/tabs/AuthorizedSignatory")),
);

const SettingsPayments = WithSuspense(
  lazy(() => import("../pages/Settings/tabs/Payments")),
);

const Security = WithSuspense(
  lazy(() => import("../pages/Settings/tabs/Security")),
);

const Plans = WithSuspense(
  lazy(() => import('../pages/Settings/tabs/Plans'))
);

const ConfirmPlans = WithSuspense(
  lazy(() => import('../pages/Settings/tabs/ConfirmPlans'))
);


const BillingDetails = WithSuspense(
  lazy(() => import("../pages/Settings/BillingDetails")),
);

const ManagePaymentMethod = WithSuspense(
  lazy(() => import("../pages/Settings/ManagePaymentMethod")),
);

const CompanyDetails = WithSuspense(
  lazy(() => import("../pages/Settings/CompanyDetails")),
);

const PublicProfile = WithSuspense(
  lazy(() => import("../pages/Settings/PublicProfile")),
);

const ProfileSaved = WithSuspense(
  lazy(() => import("../pages/Settings/ProfileSaved")),
);

const ViewProfile = WithSuspense(
  lazy(() => import("../pages/AgencyTalents/ViewProfile")),
);

const RedirectToLogin = () => {
  const location = useLocation();
  return (
    <Navigate
      to={LOGIN}
      replace
      state={{ from: location }}
    />
  );
};

// Public routes (accessible without authentication)
export const PUBLIC_ROUTES: AppRoute[] = [
  { path: LOGIN, element: <Login /> },
  { path: REGISTER, element: <Register /> },
  { path: COMPANY_INFO, element: <CompanyInfo /> },
  { path: CHANGE_PASSWORD, element: <ChangePassword /> },
  { path: VERIFY_OTP, element: <VerifyOtp /> },
  { path: FORGOT_PASS, element: <ForgotPassword /> },

  { path: FINISH_SETUP, element: <FinishSetup /> },
  { path: "*", element: <RedirectToLogin/> },
];

export const PRIVATE_ROUTES: AppRoute[] = [
  { path: DASHBOARD, element: <Dashboard /> },
  { path: AGENCY_TALENTS, element: <AgencyTalents /> },

  { path: AGENCY_TALENTS_BASIC_INFO, element: <AgencyTalentsBasicInfo /> },
  {
    path: AGENCY_TALENTS_PERSONAL_INFO,
    element: <AgencyTalentsPersonalInfo />,
  },
  {
    path: AGENCY_TALENTS_INTRO_VIDEO,
    element: <AgencyTalentsIntroVideo />,
  },
  {
    path: AGENCY_TALENTS_PAY_SETTINGS,
    element: <AgencyTalentsPaySetting />,
  },
  {
    path: EDIT_AGENCY_TALENT,
    element: <EditTalentLayout />,
    children: [
      { index: true, element: <Navigate to="basic-info" replace /> },

      { path: "basic-info", element: <AgencyTalentsBasicInfo /> },

      {
        path: "professional-experience",
        element: <AgencyTalentsPersonalInfo />,
      },

      { path: "intro-video", element: <AgencyTalentsIntroVideo /> },

      { path: "pay-settings", element: <AgencyTalentsPaySetting /> },
    ],
  },
  {
    path: UPDATE_AGENCY_TALENT,
    element: <EditTalentLayout />,
    children: [
      { index: true, element: <Navigate to="basic-info" replace /> },

      { path: "basic-info", element: <AgencyTalentsBasicInfo /> },

      {
        path: "professional-experience",
        element: <AgencyTalentsPersonalInfo />,
      },

      { path: "intro-video", element: <AgencyTalentsIntroVideo /> },

      { path: "pay-settings", element: <AgencyTalentsPaySetting /> },
    ],
  },
  {
    path: VIEW_AGENCY_TALENT,
    element: <ViewProfile />
  },
  // {
  //   path: VIEW_AGENCY_TALENT,
  //   element: <EditTalentLayout />,
  //   children: [
  //     { index: true, element: <Navigate to="basic-info" replace /> },

  //     { path: 'basic-info', element: <AgencyTalentsBasicInfo /> },

  //     {
  //       path: 'professional-experience',
  //       element: <AgencyTalentsPersonalInfo />,
  //     },

  //     { path: 'intro-video', element: <AgencyTalentsIntroVideo /> },

  //     { path: 'pay-settings', element: <AgencyTalentsPaySetting /> },
  //   ],
  // },
  { path: OFFERS, element: <Offers /> },
  { path: WORK_STREAMS, element: <WorkStreams /> },
  { path: VIEW_STREAMS_DETAILS, element: <ViewWorkStreamsDetails /> },
  { path: EARNINGS, element: <Earnings /> },
  { path: REVIEWS, element: <Reviews /> },
  { path: CLIENTS, element: <Clients /> },
  { path: CALLS, element: <Calls /> },
  { path: CHATS, element: <Chats /> },
  {
    path: SETTINGS,
    element: <SettingsLayout />,
    children: [
      { index: true, element: <Navigate to="profile" replace /> },
      { path: "profile", element: <SettingsProfile /> },
      { path: "users", element: <SettingsUsers /> },
      { path: "authorized-signatory", element: <SettingsAuthSignatory /> },
      { path: "payments", element: <SettingsPayments /> },
      { path: "security", element: <Security /> },
      { path: "billing-details", element: <BillingDetails /> },
      { path: "manage-payment-method", element: <ManagePaymentMethod /> },
    ],
  },
  { path: COMPANY_DETAILS, element: <CompanyDetails /> },
  { path: PUBLIC_PROFILE, element: <PublicProfile /> },

  { path: PROFILE_SAVED, element: <ProfileSaved /> },
  { path: "*", element: <Navigate to="/dashboard" replace /> },
];
